<template>
  <div class="w-full">
    <Text
      v-if="label"
      as="label"
      :for="inputId"
      variant="label"
      class="block mb-1"
    >
      {{ label }}
      <span
        v-if="required"
        class="text-red-500"
      >*</span>
    </Text>
    
    <div class="relative">
      <input
        :id="inputId"
        :type="inputType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        :class="inputClasses"
        :aria-label="ariaLabel || undefined"
        :aria-invalid="!!error"
        :aria-describedby="errorHelpId"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      >
      
      <!-- Icon on the left -->
      <div
        v-if="leftIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <component
          :is="leftIcon"
          :class="`h-5 w-5 ${INPUT_COLORS.icon}`"
        />
      </div>
      
      <!-- Icon on the right -->
      <div
        v-if="rightIcon || showPasswordToggle"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
      >
        <button
          v-if="showPasswordToggle"
          type="button"
          :class="`${INPUT_COLORS.iconHover} focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md`"
          :aria-label="inputType === 'password' ? t('input.showPassword') : t('input.hidePassword')"
          @click="togglePasswordVisibility"
        >
          <Icon 
            :name="inputType === 'password' ? 'eye' : 'eyeOff'"
            size="md"
            aria-hidden="true"
          />
        </button>
        <component
          :is="rightIcon"
          v-else-if="rightIcon"
          :class="`h-5 w-5 ${INPUT_COLORS.icon}`"
        />
      </div>
    </div>
    
    <!-- Error message -->
    <Text
      v-if="error"
      :id="errorHelpId"
      variant="body-sm"
      :class="`mt-1 ${INPUT_COLORS.error.text}`"
    >
      {{ error }}
    </Text>
    
    <!-- Help text -->
    <Text
      v-if="help && !error"
      :id="errorHelpId"
      variant="body-sm"
      color="muted"
      class="mt-1"
    >
      {{ help }}
    </Text>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import Text from './Text.vue'
import Icon from './Icon.vue'
import { INPUT_COLORS } from '@/constants/uiColors'
import { t } from '@/i18n/en-us'

// Generate unique ID for error/help text
const generateId = () => `input-desc-${Math.random().toString(36).substr(2, 9)}`

// Props
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  help: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  leftIcon: {
    type: [String, Object],
    default: null
  },
  rightIcon: {
    type: [String, Object],
    default: null
  },
  showPasswordToggle: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: undefined
  },
  maxlength: {
    type: Number,
    default: undefined
  },
  ariaLabel: {
    type: String,
    default: ''
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'blur', 'focus'])

// Reactive data
const inputId = ref(`input-${Math.random().toString(36).substr(2, 9)}`)
const errorHelpId = ref(generateId())
const showPassword = ref(false)

// Computed
const inputType = computed(() => {
  if (props.showPasswordToggle) {
    return showPassword.value ? 'text' : 'password'
  }
  return props.type
})

const inputClasses = computed(() => {
  const baseClasses = `block w-full rounded-md shadow-sm sm:text-sm ${INPUT_COLORS.background} ${INPUT_COLORS.text} ${INPUT_COLORS.placeholder}`
  
  const stateClasses = props.error
    ? `${INPUT_COLORS.error.border} ${INPUT_COLORS.error.focus}`
    : `${INPUT_COLORS.border} ${INPUT_COLORS.focusBorder}`
  
  const paddingClasses = props.leftIcon
    ? 'pl-10 pr-3'
    : props.rightIcon || props.showPasswordToggle
    ? 'pl-3 pr-10'
    : 'pl-3 pr-3'
  
  const disabledClasses = props.disabled
    ? INPUT_COLORS.disabled
    : ''
  
  const readonlyClasses = props.readonly
    ? INPUT_COLORS.readonly
    : ''
  
  return [
    baseClasses,
    stateClasses,
    paddingClasses,
    disabledClasses,
    readonlyClasses
  ].filter(Boolean).join(' ')
})

// Methods
const handleInput = (event) => {
  emit('update:modelValue', event.target.value)
}

const handleBlur = (event) => {
  emit('blur', event)
}

const handleFocus = (event) => {
  emit('focus', event)
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}
</script>
